export interface ColumnEllipsisConfig {
  enabled: boolean
  rows: number
  tooltip: boolean
}

export interface VxeTableColumnDef {
  field: string
  title: string
  width?: number | string
  minWidth?: number | string
  fixed?: 'left' | 'right'
  align?: 'left' | 'center' | 'right'
  resizable?: boolean
  ellipsis: ColumnEllipsisConfig
  cellClassName?: string
  isCustomSlot?: boolean
  treeNode?: boolean
}

export interface AntdLikeColumn {
  key?: string | number
  dataIndex?: string | number
  title?: string
  width?: number | string
  minWidth?: number | string
  fixed?: 'left' | 'right' | boolean
  align?: string
  /** 文本省略，默认单行；对齐 antd ellipsis */
  ellipsis?: boolean | { rows?: number; tooltip?: boolean }
  meta?: {
    needAuth?: boolean
    /** 为 true 时 columns.width 作为固定列宽；默认仅作 minWidth，列会撑满容器剩余宽度 */
    fixedWidth?: boolean
    /** 树表展开列 */
    treeNode?: boolean
  }
}

export const OPERATION_COLUMN_KEYS = new Set(['action', 'operate', 'operation'])

/** 操作列最小宽度，容纳「编辑 + 删除」等链接按钮 */
const OPERATION_COLUMN_MIN_WIDTH = 120

/**
 * 未指定 width 时，根据表头字数估算默认最小列宽：
 * 1-2 字 100px；3-4 字 120px；5-6 字 150px；7 字及以上递增
 */
export function resolveDefaultColumnMinWidth(column: AntdLikeColumn): number {
  if (column.minWidth !== undefined) {
    return Number(column.minWidth)
  }

  const field = getVxeColumnField(column)
  if (OPERATION_COLUMN_KEYS.has(field)) {
    return OPERATION_COLUMN_MIN_WIDTH
  }

  const title = column.title ?? field
  const len = title.length

  if (len <= 2) return 100
  if (len <= 4) return 120
  if (len <= 6) return 150
  return Math.min(300, 150 + (len - 6) * 15)
}

/** @deprecated 使用 resolveDefaultColumnMinWidth */
export const resolveDefaultColumnWidth = resolveDefaultColumnMinWidth

/** 解析列最小宽度：显式 minWidth / width 优先，操作列保证最小宽度 */
export function resolveColumnMinWidth(column: AntdLikeColumn): number {
  const field = getVxeColumnField(column)
  const baseMin =
    column.minWidth !== undefined
      ? Number(column.minWidth)
      : column.width !== undefined
        ? Number(column.width)
        : resolveDefaultColumnMinWidth(column)

  if (OPERATION_COLUMN_KEYS.has(field)) {
    return Math.max(baseMin, OPERATION_COLUMN_MIN_WIDTH)
  }

  return baseMin
}

/** @deprecated 使用 resolveColumnMinWidth */
export function resolveColumnWidth(column: AntdLikeColumn): number {
  return resolveColumnMinWidth(column)
}

/** 是否将 columns.width 作为 vxe 固定列宽（否则仅作 minWidth，表格会撑满容器） */
export function shouldUseFixedColumnWidth(column: AntdLikeColumn): boolean {
  return column.meta?.fixedWidth === true && column.width !== undefined
}

export function getVxeColumnField(column: AntdLikeColumn): string {
  const field = column.dataIndex ?? column.key
  return field === undefined || field === null ? '' : String(field)
}

/** 解析列级 ellipsis 配置，默认开启单行省略 */
export function resolveColumnEllipsis(column: AntdLikeColumn): ColumnEllipsisConfig {
  const { ellipsis } = column

  if (ellipsis === false) {
    return { enabled: false, rows: 1, tooltip: true }
  }

  if (ellipsis === true || ellipsis === undefined) {
    return { enabled: true, rows: 1, tooltip: true }
  }

  return {
    enabled: true,
    rows: Math.max(1, ellipsis.rows ?? 1),
    tooltip: ellipsis.tooltip !== false,
  }
}

export function formatVxeCellText(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

export function mapAntdColumnsToVxe(
  columns: AntdLikeColumn[],
  options: {
    columnResizable: boolean
    nonDraggableKeys: Set<string>
  }
): VxeTableColumnDef[] {
  return columns
    .map((column) => {
      const field = getVxeColumnField(column)
      if (!field) return null

      const fixed =
        column.fixed === 'left' || column.fixed === 'right'
          ? column.fixed
          : OPERATION_COLUMN_KEYS.has(field)
            ? 'right'
            : undefined

      const ellipsis = resolveColumnEllipsis(column)

      const minWidth = resolveColumnMinWidth(column)
      const width = shouldUseFixedColumnWidth(column) ? minWidth : undefined

      return {
        field,
        title: column.title ?? field,
        width,
        minWidth,
        fixed,
        align: (column.align as VxeTableColumnDef['align']) ?? 'center',
        resizable:
          options.columnResizable && !options.nonDraggableKeys.has(field) ? true : false,
        ellipsis,
        treeNode: column.meta?.treeNode === true,
        cellClassName: ellipsis.enabled
          ? `col--text-ellipsis col--ellipsis-rows-${ellipsis.rows}`
          : 'col--full-content',
      }
    })
    .filter((column): column is VxeTableColumnDef => Boolean(column))
}

export function buildVxeTableId(persistKey: string, userId: string | number): string {
  return `ln_vxe_${persistKey}_${userId || 'guest'}`
}

const VXE_CUSTOM_STORE_KEY = 'VXE_CUSTOM_STORE'

export function clearVxeTableCustomStorage(tableId: string): void {
  try {
    const raw = localStorage.getItem(VXE_CUSTOM_STORE_KEY)
    if (!raw) return

    const store = JSON.parse(raw) as Record<string, unknown>
    if (tableId in store) {
      delete store[tableId]
      localStorage.setItem(VXE_CUSTOM_STORE_KEY, JSON.stringify(store))
    }
  } catch {
    // ignore invalid storage
  }
}
